
import ButtonBlock from '@/components/login-register/ButtonBlock.vue';
import BCXMarkdown from '@/components/molecules/BCXMarkdown.vue';
import BCXValidatedInput from '@/components/molecules/BCXValidatedInput.vue';
import ByteCookieLogoFull from '@/components/svg/logo/logo-full-for-darkBG.svg?inline';
import useI18n from '@/mixins/useI18n';
import useBCValidators from '@/mixins/useBCValidators';
import RegistrationService from '@/services/RegistrationService';
import { LoginError } from '@/views/authentication/models';
import {
  computed, defineComponent, onMounted, reactive, ref
} from 'vue';

import useVuelidate from '@vuelidate/core';
import { Location } from 'vue-router';
import { useRoute, useRouter } from 'vue2-helpers/vue-router';
import { useStore } from 'vue2-helpers/vuex';

export default defineComponent({
  name: 'Login',
  components: {
    BCXMarkdown,
    BCXValidatedInput,
    ButtonBlock,
    ByteCookieLogoFull
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const loginError = ref<LoginError>(LoginError.NONE);
    const successMessage = ref('');
    const { t } = useI18n();

    const { emailValidation, required } = useBCValidators();

    const isPasswordAlreadyGiven = computed(() => store.state.registration.isPasswordAlreadyGiven);
    const welcomeTitleParts = computed(() => (t('login.logo_title') as string).split(/ByteCookie/));

    // const passwordRule =
    const fields = reactive({
      email: '',
      password: ''
    });

    const rules = {
      email: emailValidation,
      password: { required }
    };

    const v = useVuelidate(rules, fields);

    const submitting = ref(false);

    const submit = async () => {
      successMessage.value = '';
      const isValid = await v.value.$validate();
      if (!submitting.value && isValid) {
        const params = fields;
        submitting.value = true;
        await store.dispatch('login', params)
          .catch((error) => {
            if (error.response?.status === 401 || error.response?.status === 400) {
              switch (error.response?.data?.error?.code) {
                case 'BC-SEC-022':
                  loginError.value = LoginError.TOO_MANY_LOGINS;
                  break;
                case 'BC-SEC-012':
                  loginError.value = LoginError.ACCOUNT_SUSPENDED;
                  break;
                default:
                  loginError.value = LoginError.INVALID_LOGIN_DATA;
              }
            }
          });
        submitting.value = false;
      }
    };

    const getInviteCode = () => {
      // console.log('getInviteCode triggered');
      router.push({
        name: 'register',
        params: {
          showInvitePopup: true
        } as any
      });
    };

    const textBlock = computed(() => loginError.value || 'text_register');

    onMounted(async () => {
      const query = { ...route.query } as NonNullable<Location['query']>;

      if (query.passwordResetSuccess) {
        successMessage.value = t('error_messages.password_reset').toString();
        delete query.passwordResetSuccess;
      }

      if (route && route.query.email) {
        fields.email = route.query.email as string;
        delete query.email;
      }

      if (Object.keys(query).length !== Object.keys(route.query).length) {
        router.replace({ query });
      }

      if (isPasswordAlreadyGiven.value && !fields.email) {
        const pid = store.getters.registrationProcessId;
        const registeringUser = pid ? await RegistrationService.getRegisteringUser(pid).catch(() => null) : null;
        fields.email = registeringUser?.email ?? '';
      }
    });

    return {
      isPasswordAlreadyGiven,
      fields,
      v,
      submitting,
      welcomeTitleParts,
      textBlock,
      successMessage,
      getInviteCode,
      submit
    };
  },
});
